<template>
  <DropdownMenu v-model:open="isOpen">
    <DropdownMenuTrigger as-child>
      <Button
        :variant="triggerVariant"
        size="icon"
        class="relative h-9 w-9 shrink-0 rounded-full cursor-pointer"
      >
        <Bell class="h-[1.125rem] w-[1.125rem]" />
        <span
          v-if="unreadCount > 0"
          class="absolute -top-1 -right-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-emerald-500 px-1 text-[10px] font-bold text-white shadow"
          aria-hidden="true"
        >
          {{ unreadCount > 99 ? '99+' : unreadCount }}
        </span>
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" class="p-0 z-50">
      <Card :class="cn('w-[420px] max-w-[95vw] border-0 shadow-xl', $attrs.class ?? '')">
        <CardHeader class="p-3.5 pb-2.5 border-b border-border/40">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <CardTitle class="text-base font-semibold">{{ $t('user.common.notification') }}</CardTitle>
              <span
                v-if="unreadCount > 0"
                class="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-medium"
              >
                {{ unreadCount }} {{ $t('user.common.filterUnread') }}
              </span>
            </div>
            <div class="flex items-center gap-1">
              <Button
                v-if="unreadCount > 0"
                variant="ghost"
                size="sm"
                class="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground"
                @click="onMarkAllRead"
              >
                <CheckIcon class="mr-1 h-3 w-3" />
                {{ $t('user.common.markAllAsRead') }}
              </Button>
            </div>
          </div>

          <!-- Quick Category Filter Tabs -->
          <div class="flex items-center gap-1 pt-2 overflow-x-auto no-scrollbar">
            <button
              v-for="tab in filterTabs"
              :key="tab.id"
              class="px-2.5 py-1 text-xs font-medium rounded-md transition-colors"
              :class="activeFilter === tab.id
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'"
              @click="activeFilter = tab.id"
            >
              {{ tab.label }}
              <span v-if="tab.count != null" class="ml-1 text-[10px] opacity-80 font-mono">
                ({{ tab.count }})
              </span>
            </button>
          </div>
        </CardHeader>

        <CardContent class="p-2.5 pr-2 max-h-[420px] overflow-y-auto overscroll-contain custom-scrollbar space-y-2">
          <div v-if="filteredNotifications.length === 0" class="text-sm text-muted-foreground py-10 text-center">
            <Bell class="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
            <p>{{ $t('user.common.noNotifications') }}</p>
          </div>

          <div v-else class="space-y-1.5">
            <div
              v-for="notification in filteredNotifications"
              :key="notification.id"
              class="group relative p-2.5 rounded-lg border transition-all flex items-start gap-2.5 cursor-pointer hover:border-primary/40 hover:shadow-sm"
              :class="notification.isRead ? 'bg-muted/20 border-border/30 opacity-80' : 'bg-background border-border hover:bg-muted/40'"
              @click="onNotificationClick(notification)"
            >
              <!-- Icon by Type -->
              <div class="mt-0.5 shrink-0 rounded-full p-1.5" :class="getIconBgClass(notification.type)">
                <CreditCard v-if="notification.type === 'payment'" class="h-4 w-4 text-emerald-500" />
                <FileText v-else-if="notification.type === 'invoice'" class="h-4 w-4 text-amber-500" />
                <RefreshCw v-else-if="notification.type === 'ui-update'" class="h-4 w-4 text-sky-500" />
                <Bell v-else class="h-4 w-4 text-muted-foreground" />
              </div>

              <!-- Main Content -->
              <div class="flex-1 min-w-0 space-y-1">
                <div class="flex items-center justify-between gap-1 pr-5">
                  <p class="text-xs font-semibold truncate leading-tight text-foreground">
                    {{ notification.title }}
                  </p>
                  <span
                    v-if="!notification.isRead"
                    class="h-2 w-2 shrink-0 rounded-full bg-emerald-500 ring-2 ring-background"
                  />
                </div>
                <p class="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                  {{ notification.description }}
                </p>
                <div class="flex items-center justify-between pt-0.5 text-[10px] text-muted-foreground">
                  <div class="flex items-center gap-1.5 font-mono">
                    <span v-if="notification.branchName" class="px-1.5 py-0.2 rounded bg-muted font-sans font-medium">
                      {{ notification.branchName }}
                    </span>
                    <span v-if="notification.docNumber" class="text-primary/80 font-medium">
                      #{{ notification.docNumber }}
                    </span>
                  </div>
                  <div class="flex items-center gap-1.5 ml-auto">
                    <span class="text-muted-foreground/80">
                      {{ formatTimestamp(notification.createTime) }}
                    </span>
                    <ExternalLink
                      v-if="notification.type === 'payment' || notification.type === 'invoice'"
                      class="h-3 w-3 text-muted-foreground/40 group-hover:text-primary transition-colors"
                      :title="$t('user.common.clickToSearch')"
                    />
                  </div>
                </div>

                <!-- Action Button for UI Updates -->
                <div v-if="notification.type === 'ui-update'" class="pt-1.5">
                  <Button
                    size="sm"
                    class="h-7 text-xs px-2.5"
                    :disabled="refreshing"
                    @click.stop="onRefreshUi(notification.remoteVersion)"
                  >
                    {{ refreshing ? 'Refreshing…' : 'Refresh Now' }}
                  </Button>
                </div>
              </div>

              <!-- Individual Remove Button (X) -->
              <button
                type="button"
                class="absolute top-2 right-2 p-1 rounded-md text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10 transition-colors cursor-pointer"
                :title="$t('user.common.remove')"
                @click.stop="onRemoveNotification(notification.id)"
              >
                <X class="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </CardContent>

        <CardFooter class="p-2.5 border-t border-border/40 flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            class="text-xs h-8 text-muted-foreground hover:text-foreground"
            :disabled="notifications.length === 0"
            @click="onClearAll"
          >
            {{ $t('user.common.clearAll') }}
          </Button>
          <span class="text-[11px] text-muted-foreground/60 font-mono">
            {{ notifications.length }} items
          </span>
        </CardFooter>
      </Card>
    </DropdownMenuContent>
  </DropdownMenu>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, type PropType } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { Bell, CreditCard, FileText, RefreshCw, CheckIcon, X, ExternalLink } from 'lucide-vue-next'
import { Button, type ButtonVariants } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { cn } from '@/lib/utils'
import { useNotificationStore, type AppNotification } from '@/core/store/notification'
import { applyUiUpdateReload } from '@/core/app/uiVersionWatch'

defineProps({
  triggerVariant: {
    type: String as PropType<NonNullable<ButtonVariants['variant']>>,
    default: 'outline' as const,
  },
})

const router = useRouter()
const { t } = useI18n()
const notifStore = useNotificationStore()
const refreshing = ref(false)
const isOpen = ref(false)
const activeFilter = ref<'all' | 'payment' | 'invoice' | 'unread'>('all')

onMounted(() => {
  notifStore.initSocketListeners()
})

const notifications = computed(() => notifStore.notifications)
const unreadCount = computed(() => notifStore.unreadCount)

const filterTabs = computed(() => [
  { id: 'all' as const, label: t('user.common.filterAll'), count: notifications.value.length },
  {
    id: 'payment' as const,
    label: t('user.common.filterPayments'),
    count: notifications.value.filter((n) => n.type === 'payment').length,
  },
  {
    id: 'invoice' as const,
    label: t('user.common.filterInvoices'),
    count: notifications.value.filter((n) => n.type === 'invoice').length,
  },
  { id: 'unread' as const, label: t('user.common.filterUnread'), count: unreadCount.value },
])

const filteredNotifications = computed(() => {
  const list = notifications.value
  switch (activeFilter.value) {
    case 'payment':
      return list.filter((n) => n.type === 'payment')
    case 'invoice':
      return list.filter((n) => n.type === 'invoice')
    case 'unread':
      return list.filter((n) => !n.isRead)
    default:
      return list
  }
})

function getIconBgClass(type?: string) {
  switch (type) {
    case 'payment':
      return 'bg-emerald-500/15'
    case 'invoice':
      return 'bg-amber-500/15'
    case 'ui-update':
      return 'bg-sky-500/15'
    default:
      return 'bg-muted'
  }
}

function formatTimestamp(timeStr?: string) {
  if (!timeStr) return ''
  try {
    const d = new Date(timeStr)
    const diffMs = Date.now() - d.getTime()
    const diffSec = Math.floor(diffMs / 1000)
    if (diffSec < 60) return 'Just now'
    const diffMin = Math.floor(diffSec / 60)
    if (diffMin < 60) return `${diffMin}m ago`
    const diffHours = Math.floor(diffMin / 60)
    if (diffHours < 24) return `${diffHours}h ago`
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  } catch {
    return ''
  }
}

function onNotificationClick(notif: AppNotification) {
  if (!notif.isRead) {
    notifStore.markAsRead(notif.id)
  }

  if (notif.type === 'ui-update') {
    onRefreshUi(notif.remoteVersion)
    return
  }

  // Navigate to Billing checklist search
  const term = notif.searchTerm || notif.docNumber || notif.customerName || notif.referenceNo || notif.qbCustomerId
  if (term) {
    isOpen.value = false // close popover
    router.push({
      path: '/billing',
      query: {
        page: '1',
        limit: '10',
        search: String(term).trim(),
        dateFilter: 'all',
      },
    })
  }
}

function onRemoveNotification(id: string) {
  notifStore.removeNotification(id)
}

function onMarkAllRead() {
  notifStore.markAllAsRead()
}

function onClearAll() {
  notifStore.clearAll()
}

async function onRefreshUi(remoteVersion?: string) {
  if (!remoteVersion || refreshing.value) return
  refreshing.value = true
  try {
    await applyUiUpdateReload(remoteVersion)
  } finally {
    refreshing.value = false
  }
}
</script>

<style scoped>
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: hsl(var(--muted-foreground) / 0.3) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: hsl(var(--muted-foreground) / 0.3);
  border-radius: 9999px;
  transition: background-color 0.2s ease;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: hsl(var(--muted-foreground) / 0.55);
}
</style>
